import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Filter,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Sparkles,
  BarChart3,
  TrendingUp,
  Target,
  Users,
  Award,
  Globe,
  Clock,
  ShieldCheck,
  Check,
  Building2,
  FileCheck,
  Search,
  Zap,
  Layout,
  MousePointerClick,
  Activity,
  Layers,
  Sliders,
  DollarSign,
} from "lucide-react";

export const Route = createFileRoute("/services_/cro")({
  head: () => ({
    meta: [
      {
        title:
          "Conversion Rate Optimisation (CRO) Services — KMWAdvertising · A/B Testing & UX",
      },
      {
        name: "description",
        content:
          "Conversion Rate Optimisation (CRO) Services That Turn More Visitors into Customers. Landing Page UX, A/B Testing, Heatmaps & Funnel Audits by KMWA LLP.",
      },
      {
        property: "og:title",
        content: "Conversion Rate Optimisation (CRO) Services — KMWAdvertising",
      },
      {
        property: "og:description",
        content:
          "Maximise the Value of Every Website Visitor. Get more qualified leads and sales without increasing your advertising budget.",
      },
    ],
  }),
  component: CROServicePage,
});

export function CROServicePage() {
  const whyCROMatters = [
    {
      title: "Increase Lead Generation",
      desc: "Convert a higher percentage of existing website traffic into qualified leads, bookings, and sales inquiries.",
      icon: Target,
    },
    {
      title: "Improve Online Sales & AOV",
      desc: "Optimize product pages, checkout flows, and user navigation to reduce cart abandonment and increase eCommerce revenue.",
      icon: TrendingUp,
    },
    {
      title: "Lower Customer Acquisition Cost (CAC)",
      desc: "Extract significantly higher revenue from your existing ad spend across Google Ads, Meta Ads, and SEO.",
      icon: DollarSign,
    },
    {
      title: "Enhance User Experience (UX)",
      desc: "Eliminate navigation confusion, slow load times, and friction to create intuitive, enjoyable customer journeys.",
      icon: Layout,
    },
    {
      title: "Maximize Cross-Channel Marketing ROI",
      desc: "Ensure every ad click leads to a high-converting landing page optimized for maximum return on ad spend.",
      icon: BarChart3,
    },
  ];

  const croServicesSuite = [
    {
      title: "CRO Audit & Funnel Analysis",
      desc: "Comprehensive review of landing page UX, user journeys, funnel drop-off points, mobile usability, and tracking validation.",
      icon: Filter,
    },
    {
      title: "Landing Page Engineering",
      desc: "Optimizing headlines, value propositions, call-to-action (CTA) buttons, trust badges, form fields, and visual hierarchy.",
      icon: Layout,
    },
    {
      title: "User Experience (UX) Optimisation",
      desc: "Navigation restructuring, page speed enhancements, mobile experience tuning, content readability, and WCAG accessibility.",
      icon: MousePointerClick,
    },
    {
      title: "A/B & Multivariate Testing",
      desc: "Data-backed controlled experiments testing headlines, CTAs, hero visuals, forms, pricing displays, and checkout layouts.",
      icon: Sliders,
    },
    {
      title: "Behaviour Analytics & Heatmaps",
      desc: "Deep click tracking, scroll maps, session recordings, form drop-off analytics, and exit page behavior analysis.",
      icon: Activity,
    },
    {
      title: "Conversion Tracking & Attribution",
      desc: "End-to-end tracking for forms, phone calls, purchases, downloads, and revenue attribution across Google Analytics 4.",
      icon: Target,
    },
  ];

  const abTestableElements = [
    "Headlines & Value Propositions",
    "Call-to-Action (CTA) Copy & Button Colors",
    "Hero Layouts & Background Media",
    "Lead Form Fields & Multi-step Flows",
    "Product Images & Demo Videos",
    "Social Proof, Reviews & Trust Signals",
    "Pricing Tables & Offer Structures",
    "Navigation Menu & Cart Checkout UX",
  ];

  const croToolsList = [
    "Google Analytics 4 (GA4)",
    "Google Tag Manager (GTM)",
    "Google Looker Studio",
    "Microsoft Clarity",
    "Hotjar Heatmaps",
    "VWO (Visual Website Optimizer)",
    "Optimizely",
    "Google Search Console",
    "Session Recording & Click Tools",
  ];

  const kmwaStrengths = [
    "25+ years of strategic advertising & conversion optimization leadership",
    "Certified CRO & UX/UI design specialists",
    "Advanced behavioral analytics & session recording expertise",
    "Scientific data-driven A/B testing methodology",
    "Landing page conversion engineering focus",
    "Cross-channel integration with Google Ads, Meta Ads & SEO",
    "AI-assisted heatmaps & customer drop-off diagnostics",
    "100% transparent reporting with measurable conversion lift",
    "Focus on increasing sales without raising ad budgets",
    "Continuous post-launch CRO iterative optimization",
  ];

  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-14 sm:py-20 md:py-24 border-b border-white/10">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-purple)] opacity-25" />
          <div className="glow-orb right-[-100px] top-20 h-[400px] w-[400px] bg-[var(--glow-cyan)] opacity-25" />

          <div className="container-x relative z-10">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2.5 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">
                Home
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <Link to="/services" className="hover:text-white transition">
                Services
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-[var(--glow-purple)] font-semibold">Conversion Optimisation (CRO)</span>
            </div>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-6">
                <Filter className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
                Digital Media Capabilities
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                Conversion Rate Optimisation (CRO) Services That Turn More Visitors into Customers
              </h1>

              <p className="mt-4 text-base font-mono text-white/80 uppercase tracking-widest">
                Landing Page UX, A/B Testing, Heatmaps & Funnel Audits
              </p>

              <p className="mt-4 text-lg sm:text-xl font-accent italic text-[var(--glow-purple)] font-medium border-l-2 border-[var(--glow-purple)] pl-4 py-1">
                Maximise the Value of Every Website Visitor
              </p>

              <div className="mt-6 space-y-4 text-base sm:text-lg text-white/90 font-normal leading-relaxed">
                <p>
                  Driving traffic to your website is only half the challenge. The real measure of digital marketing success is how effectively that traffic converts into inquiries, qualified leads, online sales, and subscriptions.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Many businesses invest heavily in SEO, Google Ads, Meta Ads and social media marketing, yet struggle with low conversion rates due to friction in user experience. This leads to wasted ad spend and high customer acquisition costs.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  At <strong className="text-white">KMWAdvertising LLP</strong>, we help businesses improve website performance through strategic CRO. By combining UX design, heatmaps, session recordings, A/B testing, and persuasive copywriting, we convert more visitors without requiring larger ad budgets.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="/contact-us" className="btn-hero !py-3.5 !px-7">
                  Request CRO Website Audit
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/services" className="btn-ghost !py-3.5 !px-7">
                  Explore All Services
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* WHY CRO MATTERS */}
        <section className="py-14 sm:py-20 border-b border-white/10 bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Maximizing ROI from Existing Traffic
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                Why Conversion Rate Optimisation Matters
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                Improving conversion rates often delivers a significantly higher return on investment than simply buying more advertising traffic.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {whyCROMatters.map((item, idx) => {
                const CIcon = item.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)]">
                          PILLAR 0{idx + 1}
                        </span>
                        <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                          <CIcon className="h-5 w-5 text-[var(--glow-purple)]" />
                        </div>
                      </div>
                      <h3 className="font-display text-xl font-bold text-white mb-2">
                        {item.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* SERVICES SUITE & A/B TESTING */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Structured CRO Framework
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Our Conversion Rate Optimisation Services
              </h2>
            </div>

            {/* 6 SERVICES CARDS */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {croServicesSuite.map((svc, idx) => {
                const SIcon = svc.icon;
                return (
                  <div key={idx} className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 space-y-4">
                    <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[var(--glow-purple)]">
                      <SIcon className="h-5 w-5" />
                    </div>
                    <h4 className="font-display text-xl font-bold text-white">{svc.title}</h4>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{svc.desc}</p>
                  </div>
                );
              })}
            </div>

            {/* A/B ELEMENTS & CRO TOOLS */}
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              {/* Elements We Test */}
              <div className="lg:col-span-6 p-8 rounded-3xl glass-strong border border-white/10 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <Sliders className="h-5 w-5 text-[var(--glow-purple)]" />
                  Key Elements We A/B Test
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Controlled testing on page variables that directly impact user conversion rates:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                  {abTestableElements.map((elem, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                      <span>{elem}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tools We Use */}
              <div className="lg:col-span-6 p-8 rounded-3xl glass-strong border border-white/10 bg-[#06060c]/90 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <Activity className="h-5 w-5 text-sky-400" />
                  CRO Tools & Heatmap Tech
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  We deploy industry-leading session recording, heatmap, and split-testing platforms:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                  {croToolsList.map((tool, i) => (
                    <div key={i} className="p-2.5 rounded-xl bg-white/[0.03] border border-white/5 text-xs text-white/90 font-medium">
                      {tool}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHY CHOOSE KMWA LLP */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                25+ Years Advertising & Conversion Heritage
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Why Choose KMWAdvertising LLP?
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {kmwaStrengths.map((str, i) => (
                <div key={i} className="p-4 rounded-xl bg-white/[0.03] border border-white/5 space-y-2">
                  <span className="font-mono text-xs text-[var(--glow-purple)] font-bold">0{i + 1}</span>
                  <p className="text-xs text-white/90 leading-snug font-medium">{str}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CLOSING CTA BANNER */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-purple)] opacity-20" />

          <div className="container-x relative z-10 text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-muted-foreground border border-white/10 mb-6">
              <Filter className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
              Conversion Optimisation Advisory
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Get More Leads and Sales Without Increasing Your Advertising Budget
            </h2>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              If your website attracts visitors but isn't generating enough inquiries, contact our CRO specialists today for a comprehensive conversion audit.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact-us" className="btn-hero w-full sm:w-auto">
                Request CRO Audit Proposal
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/services" className="btn-ghost w-full sm:w-auto">
                Back to All Services
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
